import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, ExternalLink, CheckCircle, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PublicReviewPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewClicked, setReviewClicked] = useState(false);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reviews/public/campaign/${campaignId}`);
        setCampaign(res.data);
      } catch (err) {
        setError('Campaign not found or has expired.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId]);

  const handleReviewClick = async (platform, url) => {
    try {
      // Mark that they clicked to leave a review
      await axios.post(`${BACKEND_URL}/api/reviews/public/mark-review/${campaignId}?platform=${platform}`);
      setReviewClicked(true);
      
      // Open review URL in new tab
      window.open(url, '_blank');
    } catch (err) {
      // Still open the URL even if tracking fails
      window.open(url, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-white mb-2">Oops!</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (reviewClicked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Thank You!</h1>
          <p className="text-slate-300 text-lg mb-6">
            We truly appreciate you taking the time to share your experience with {campaign.contractor_company || campaign.contractor_name}.
          </p>
          <p className="text-slate-400">
            Your feedback helps other homeowners find quality roofing services.
          </p>
        </div>
      </div>
    );
  }

  const hasAnyReviewUrl = campaign.google_review_url || campaign.facebook_review_url || campaign.yelp_review_url;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">
            How Was Your Experience?
          </h1>
          <p className="text-slate-300 text-lg">
            Hi {campaign.customer_name?.split(' ')[0] || 'there'}! We'd love to hear about your roofing project with{' '}
            <span className="text-blue-400 font-semibold">
              {campaign.contractor_company || campaign.contractor_name}
            </span>
          </p>
        </div>

        {/* Review Buttons */}
        {hasAnyReviewUrl ? (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-8 border border-slate-700">
            <p className="text-center text-slate-300 mb-6">
              Please choose your preferred platform to leave a review:
            </p>
            
            <div className="space-y-4">
              {campaign.google_review_url && (
                <button
                  onClick={() => handleReviewClick('google', campaign.google_review_url)}
                  className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02]"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Review on Google
                  <ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                </button>
              )}

              {campaign.facebook_review_url && (
                <button
                  onClick={() => handleReviewClick('facebook', campaign.facebook_review_url)}
                  className="w-full flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02]"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Review on Facebook
                  <ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                </button>
              )}

              {campaign.yelp_review_url && (
                <button
                  onClick={() => handleReviewClick('yelp', campaign.yelp_review_url)}
                  className="w-full flex items-center justify-center gap-3 bg-[#D32323] hover:bg-[#C41200] text-white font-semibold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02]"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.715-.8-1.118-1.59l2.904-3.853c.597-.79 1.814-.39 1.802.593l-.054 2.973c-.007.384-.217.338-.539.444zm-8.428 5.97l1.31-5.03c.254-.975 1.643-1.097 2.06-.18l2.022 4.453c.417.916-.504 1.828-1.363 1.35l-3.455-1.92c-.343-.19-.66-.177-.574-.673zm-2.315-5.09l-3.727-3.43c-.723-.666-.292-1.85.64-1.753l4.52.468c.93.097 1.23 1.327.443 1.82l-4.78 2.96c.32-.19-.243-.4.904-.065zm1.55-7.053l1.59 4.907c.308.952-.68 1.77-1.465 1.212l-3.814-2.713c-.785-.558-.385-1.794.593-1.832l3.096-.115c.39-.015.39-.215 0-.46zm7.9 3.222l-4.773 1.77c-.926.343-1.78-.628-1.266-1.439l2.505-3.95c.514-.81 1.75-.533 1.83.412l.39 3.035c.038.287-.365.055-.686.172z"/>
                  </svg>
                  Review on Yelp
                  <ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-8 border border-slate-700 text-center">
            <p className="text-slate-300">
              Thank you for your interest in leaving a review! Please contact{' '}
              {campaign.contractor_company || campaign.contractor_name} directly.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm flex items-center justify-center gap-2">
            <Heart className="w-4 h-4 text-red-400" />
            Your feedback means the world to us
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicReviewPage;
